/**
 * 🎨 CSS to React Native StyleSheet Converter
 * Helps convert web CSS classes to React Native styles
 */

import { StyleSheet, ViewStyle, TextStyle, ImageStyle } from 'react-native';

/**
 * Coach Cedar Design System Colors
 * Official Brand Colors from DESIGN_TOKENS.md
 * 
 * PRIMARY PALETTE:
 * - Cedar Prime (#1F6C4F): Main brand green (60% usage)
 * - Limestone (#F3F3F3): Page backgrounds (20% usage)
 * - Cedar Stream (#42A5A5): Coach signals, active states (15% usage)
 * - Dark Cedar (#113C2B): Depth accents (10% usage)
 * - Cedar Ink (#0B1614): Body text, UI labels (5% usage)
 * 
 * DERIVED COLORS:
 * - Cedar Light (#5FA287): ⭐ USE FOR BUTTONS - pops more than Cedar Subtle
 * - Cedar Subtle (#E8F2ED): Soft backgrounds, user bubbles
 * - Stream Hover (#5ABFBF): Cedar Stream hover state
 * - Stream Pressed (#378F8F): Cedar Stream pressed state
 */
export const CoachCedarColors = {
  // ═══════════════════════════════════════════════════════════════
  // 🎯 SPEC-CANONICAL NAMES (Move Section Native Spec)
  // New native code should prefer these names.
  // Values verified against web CSS variables (index.css) and spec.
  // ═══════════════════════════════════════════════════════════════
  primary: '#1F6C4F',         // Cedar green — primary actions, active states  (--cedar-primary)
  primaryDark: '#113C2B',     // Dark cedar — headers, emphasis                (--cedar-dark)
  primaryLight: '#5FA287',    // Light cedar — hover, secondary                (cedarLight)
  accent: '#42A5A5',          // KEEP-only (Thrive migration 2026-06): rest-day/data-viz teal. NOT accent chrome — use thrive/cedarPrime.  (--cedar-accent-sky)
  accentSky: '#87CEEB',       // Sky blue — rest day recovery accents
  surface: '#FFFFFF',         // White — card backgrounds
  surfaceLight: '#F3F3F3',    // Limestone — section backgrounds               (--cedar-limestone)
  surfaceSubtle: '#E8F2ED',   // Cedar subtle — soft highlights                (--cedar-subtle)
  text: '#0B1614',            // Cedar ink — primary text                      (--cedar-forest-deep)
  textSecondary: '#6B7280',   // Gray — secondary text, labels
  textTertiary: '#9CA3AF',    // Light gray — timestamps, hints
  border: '#E5E7EB',          // Light border
  success: '#10B981',         // Green — completed states
  warning: '#F59E0B',         // Amber — attention needed
  error: '#EF4444',           // Red — errors, missed
  errorLight: '#FEE2E2',       // Light red — error backgrounds
  errorDark: '#DC2626',       // Darker red — destructive button pressed state (Tailwind red-600)
  restDay: '#0EA5E9',         // Sky blue — rest day cards
  yogaPurple: 'rgba(120, 52, 181, 1)', // Purple — yoga mobility icon (--coloryogatxt)
  cardioOrange: '#E87722',    // Orange — cardio medium/default intensity (nativeWorkoutIconMapper)

  // ═══════════════════════════════════════════════════════════════
  // 🌲 BRAND PALETTE (Official naming from DESIGN_TOKENS.md)
  // ═══════════════════════════════════════════════════════════════
  cedarPrime: '#1F6C4F',      // Main brand green (60% usage) - buttons, CTAs, logo
  limestone: '#F3F3F3',       // Page backgrounds (20% usage) - breathing room
  cedarStream: '#42A5A5',     // KEEP-only after Thrive migration (2026-06): rest-day/data-viz/activity teal. NOT for accent — use thrive (fill) / cedarPrime (text/icon on light).
  darkCedar: '#113C2B',       // Depth accents (10% usage) - premium moments, drawers
  cedarInk: '#0B1614',        // Body text (5% usage) - text, labels, icons

  // 🌿 NEW PALETTE (2026-06) — teal → Thrive migration (Cedar Prime kept)
  thrive: '#3BD49B',          // Thrive — mint accent (replaces cedarStream/teal)
  thriveDeep: '#2BAE82',      // Darker Thrive — pressed/active state for Thrive buttons
  thriveHover: '#5FE0B5',     // Lighter Thrive — hover state (migration target for streamHover #5ABFBF)
  roots: '#12402F',           // Roots — deepest brand green
  freshAir: '#FCFCFC',        // Fresh Air — near-white surface
  drive: '#FF5666',           // Drive — coral (secondary)
  heat: '#D0603E',            // Heat — terracotta (accent)
  reset: '#C2AFF0',           // Reset — lavender (accent)

  // 🎨 DERIVED COLORS (Official)
  cedarLight: '#5FA287',      // ⭐ USE FOR BUTTONS - success states, growth moments
  cedarLightPressed: 'rgba(95, 162, 135, 0.7)', // cedarLight at 0.7 — pressed state for completed cards using cedarSubtle
  cedarMist: '#A8D1BF',       // Mid-tone green — borders, dividers, soft accents
  cedarPrimeAtRest: '#BCD3CA', // cedarPrime composited at 30% over white — EXACTLY the thinking dots at rest (TypingIndicator renders cedarPrime dots at opacity 0.3). Flat hex (not rgba) so MaskedView gradients render deterministically. Keep in lockstep with cedarPrime + TypingIndicator's resting opacity.
  cedarSubtle: '#E8F2ED',     // Soft backgrounds, user message bubbles
  streamHover: '#5FE0B5',     // @deprecated (Thrive migration 2026-06) — aliased to thriveHover. Do not use; kept for graceful fallback of any missed usage.
  streamPressed: '#2BAE82',   // @deprecated (Thrive migration 2026-06) — aliased to thriveDeep. Do not use; kept for graceful fallback of any missed usage.

  // 🩶 NEUTRAL — empty / inactive UI states.
  // Not part of the Cedar palette but reused enough across UI surfaces
  // (week-strip empty dots, disabled placeholders, etc.) to deserve a
  // single source of truth. Identical to Material Design's Gray 300;
  // distinct from `border` (#E5E7EB) which is reserved for actual borders.
  neutralGray: '#E0E0E0',     // Empty/inactive placeholder gray
  // ═══════════════════════════════════════════════════════════════
  // 🔄 LEGACY ALIASES (backward compatibility — do NOT remove)
  // ═══════════════════════════════════════════════════════════════
  cedarDark: '#113C2B',       // Alias for darkCedar / primaryDark

  // UI Colors (legacy names → use surface*/text* spec names in new code)
  background: '#FFFFFF',      // → surface
  backgroundLight: '#F9FAFB',
  backgroundDark: '#F3F3F3',  // → surfaceLight (Limestone)

  textLight: '#6B7280',       // → textSecondary
  textLighter: '#9CA3AF',     // → textTertiary

  borderLight: '#F3F4F6',
  borderDark: '#D1D5DB',
  // Subtle hairline divider used for section breaks ("WORKOUT PLAN"
  // separator etc.). Was hardcoded as rgba(0,0,0,0.08) inline in
  // WorkoutDetail.native.tsx — tokenised here so the value lives in the
  // design system, not in components.
  divider: 'rgba(0, 0, 0, 0.08)',

  // Semantic (legacy)
  info: '#3B82F6',

  // Chat-specific
  cedarBubble: '#FFFFFF',
  cedarAvatar: '#1F6C4F',
  userBubble: '#E8F2ED',     // User message bubbles (same as cedarSubtle)
};

/**
 * Canonical HR Zone color palette.
 * All HR zone visualizations should import from here.
 */
export const HRZoneColors = {
  zone1: '#3B82F6',  // Blue — Recovery
  zone2: '#22C55E',  // Green — Aerobic
  zone3: '#F59E0B',  // Amber — Tempo
  zone4: '#F97316',  // Orange — Threshold
  zone5: '#EF4444',  // Red — VO2 Max
} as const;
export const HR_ZONE_COLOR_ARRAY = Object.values(HRZoneColors);

/**
 * Common spacing values (4px base grid)
 *
 * Spec-canonical names: xs → xxxl (use in new native code)
 * Legacy aliases: '2xl', '3xl', '4xl' (kept for backward compat)
 */
export const Spacing = {
  xxs: 2,
  xsm: 6,
  xs: 4,
  sm: 8,
  smd: 10,
  md: 12,
  mld: 14,
  lg: 16,
  xl: 20,
  xxl: 24,       // Spec-canonical (prefer over '2xl')
  xxxl: 32,      // Spec-canonical (prefer over '3xl')
  '2xl': 24,     // Legacy alias → xxl
  '3xl': 32,     // Legacy alias → xxxl
  '4xl': 40,     // Legacy (not in spec, kept for compat)
};

/**
 * Common border radius values (legacy — Tailwind-aligned)
 */
export const BorderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  full: 9999,
};

/**
 * Spec-canonical border radius (Move Section spec)
 * New native code should prefer these values.
 */
export const Radius = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  full: 9999,
};

/**
 * Card icon/check circle sizing (native cards)
 * Use these so icon and check circles align across GoalCard, SimpleWorkoutCard, SimpleMobilityCard, RestDayCardWrapper.
 */
export const CARD_ICON_CIRCLE_SIZE = 40;
export const CARD_CHECK_MIN_SIZE = 44;
/** Icon/activity icon: square with rounded corners (not circle). */
export const CARD_BORDER_RADIUS_ICON = 12;
export const CARD_BORDER_RADIUS_CHECK = 22;

/**
 * Opacity tokens — semantically named, not tagged by component.
 *
 * New convention (M35B audit follow-up, 2026-04-26): opacity values
 * that carry visual *meaning* (not transient gestures like press
 * feedback) should be tokenised here rather than inlined as literals.
 *
 * Add new entries when you find a value that:
 *   - Conveys state semantics (e.g., "missed", "disabled", "pending")
 *   - Is reused across multiple components, OR
 *   - Is part of the design language and should stay tunable in one place
 *
 * Don't tokenise: pressed-state opacity (0.7-0.85) — those are
 * gesture-feedback details, not semantic state.
 *
 * If you add a new entry, also document the visual it pairs with
 * (background colour, font weight, etc.) so the design language stays
 * coherent.
 */
export const Opacity = {
  /**
   * Missed-workout icon on the week-strip dot row.
   *
   * Pairs with `CoachCedarColors.cedarMist` background. Reads as
   * clearly faded (the day was missed) while keeping the workout
   * icon recognizable at 14pt. Source: Ticket 3 / Call 1.
   *
   * Renderer: `WeekStripDots.native.tsx` PrimaryIcon path.
   */
  missedIcon: 0.45,
} as const;

// ─── Background luminance helper ─────────────────────────────────────
//
// Used by overlay UI (campaign modals, drawers) to pick a readable
// X-close foreground for any background. Avoids one-off branches like
// `if preset_id === 'cedar_bold'` — works for ANY hex or gradient.
//
// W3C relative-luminance formula simplified to integer arithmetic:
//   L = 0.299·R + 0.587·G + 0.114·B   (range 0–255)
//   dark if L < 128
//
// For gradients, we treat them as dark by default (current Cedar
// Gradient is darkCedar/cedarInk). If a future preset introduces a
// light-stop gradient, pass `isGradient: false` or extend the helper.
// ─────────────────────────────────────────────────────────────────────

/** Parse a `#RRGGBB` (or `#RGB`) string into [r, g, b] ints. Returns
 * null for any non-hex input (e.g., 'transparent', rgba(...) strings).
 * Caller decides how to handle null. */
function _parseHex(hex: string | null | undefined): [number, number, number] | null {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/**
 * True if a background should be treated as dark (i.e., the X-close
 * icon and any text overlays should switch to light foreground).
 *
 * @param background  A hex color string (e.g., '#113C2B') OR null/undefined
 *                    to mean "no solid background set"
 * @param hasGradient Whether the surface uses a gradient background
 *                    (gradients are treated as dark by default — every
 *                    current gradient preset is dark-stopped)
 *
 * Returns false (light) when input is unparseable so callers default to
 * the standard light-theme X (visually correct on the most common case
 * of a white modal); callers that need stricter behavior can null-check
 * the background themselves.
 */
export function isBackgroundDark(
  background: string | null | undefined,
  hasGradient: boolean = false,
): boolean {
  if (hasGradient) return true;
  const rgb = _parseHex(background);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 128;
}

/**
 * Typography scale
 *
 * Size-based names (xs → 4xl): Legacy Tailwind-aligned, kept for compat.
 * Semantic names (h1 → caption): Spec-canonical, use in new native code.
 */
export const Typography = {
  // ── Legacy size-based (backward compat) ──
  xs: { fontSize: 12, lineHeight: 16 },
  sm: { fontSize: 14, lineHeight: 20 },
  base: { fontSize: 16, lineHeight: 24 },
  lg: { fontSize: 18, lineHeight: 28 },
  xl: { fontSize: 20, lineHeight: 28 },
  '2xl': { fontSize: 24, lineHeight: 32 },
  '3xl': { fontSize: 30, lineHeight: 36 },
  '4xl': { fontSize: 36, lineHeight: 40 },

  // ── Spec-canonical semantic names (new native code) ──
  h1: { fontSize: 24, fontWeight: '700' as const, lineHeight: 32 },
  h2: { fontSize: 20, fontWeight: '600' as const, lineHeight: 28 },
  h3: { fontSize: 17, fontWeight: '600' as const, lineHeight: 24 },
  bodyLarge: { fontSize: 16, fontWeight: '400' as const, lineHeight: 24 },
  body: { fontSize: 15, fontWeight: '400' as const, lineHeight: 22 },
  bodySmall: { fontSize: 13, fontWeight: '400' as const, lineHeight: 18 },
  caption: { fontSize: 11, fontWeight: '500' as const, lineHeight: 16 },
  label: { fontSize: 13, fontWeight: '600' as const, lineHeight: 18 },

  // ── Display tokens (timers, hero numbers) ──
  displaySm: { fontSize: 28, fontWeight: '700' as const, lineHeight: 34 },
  displayMd: { fontSize: 36, fontWeight: '700' as const, lineHeight: 42 },
  displayLg: { fontSize: 48, fontWeight: '700' as const, lineHeight: 56 },
  displayXL: { fontSize: 56, fontWeight: '700' as const, lineHeight: 64 },
};

/**
 * Common shadow styles (approximates Tailwind shadows)
 */
/**
 * Shadow presets (legacy — kept for backward compat)
 */
export const Shadows = {
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  lg: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 5,
  },
  xl: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 10,
    elevation: 8,
  },
};

/**
 * Spec-canonical shadow presets (Move Section spec)
 * New native code should prefer these values.
 */
export const Shadow = {
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 3,
  },
  lg: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 5,
  },
};

/**
 * Shared card styling base (Move Section spec)
 *
 * Usage:
 *   const styles = StyleSheet.create({
 *     card: { ...CardStyles.bordered, ...overrides },
 *   });
 *
 * Presets:
 *   base     – background, radius, padding, shadow (no border)
 *   bordered – base + 1px border (workout / mobility cards)
 */
export const CardStyles = {
  /** Shared base: white surface, rounded corners, padding, subtle shadow */
  base: {
    backgroundColor: CoachCedarColors.surface,
    borderRadius: Radius.lg,
    padding: Spacing.lg,
    ...Shadow.sm,
  } as const,

  /** Standard bordered card (workout, mobility) */
  bordered: {
    backgroundColor: CoachCedarColors.surface,
    borderRadius: Radius.lg,
    padding: Spacing.lg,
    ...Shadow.sm,
    borderWidth: 1,
    borderColor: CoachCedarColors.borderLight,
  } as const,
};

/**
 * Convert Tailwind-style class names to React Native styles
 */
export const convertTailwindToStyle = (className: string): any => {
  const styles: any = {};
  const classes = className.split(' ');

  classes.forEach(cls => {
    // Padding
    if (cls.match(/^p-(\d+)$/)) {
      const value = parseInt(cls.match(/^p-(\d+)$/)![1]) * 4;
      styles.padding = value;
    }
    if (cls.match(/^px-(\d+)$/)) {
      const value = parseInt(cls.match(/^px-(\d+)$/)![1]) * 4;
      styles.paddingHorizontal = value;
    }
    if (cls.match(/^py-(\d+)$/)) {
      const value = parseInt(cls.match(/^py-(\d+)$/)![1]) * 4;
      styles.paddingVertical = value;
    }
    
    // Margin
    if (cls.match(/^m-(\d+)$/)) {
      const value = parseInt(cls.match(/^m-(\d+)$/)![1]) * 4;
      styles.margin = value;
    }
    if (cls.match(/^mx-(\d+)$/)) {
      const value = parseInt(cls.match(/^mx-(\d+)$/)![1]) * 4;
      styles.marginHorizontal = value;
    }
    if (cls.match(/^my-(\d+)$/)) {
      const value = parseInt(cls.match(/^my-(\d+)$/)![1]) * 4;
      styles.marginVertical = value;
    }
    
    // Background colors
    if (cls === 'bg-white') styles.backgroundColor = '#FFFFFF';
    if (cls === 'bg-gray-50') styles.backgroundColor = '#F9FAFB';
    if (cls === 'bg-gray-100') styles.backgroundColor = '#F3F4F6';
    if (cls.startsWith('bg-green-')) {
      if (cls === 'bg-green-600') styles.backgroundColor = CoachCedarColors.primary;
      if (cls === 'bg-green-500') styles.backgroundColor = CoachCedarColors.primaryLight;
      if (cls === 'bg-green-700') styles.backgroundColor = CoachCedarColors.primaryDark;
    }
    
    // Text colors
    if (cls === 'text-white') styles.color = '#FFFFFF';
    if (cls === 'text-gray-900') styles.color = CoachCedarColors.text;
    if (cls === 'text-gray-600') styles.color = CoachCedarColors.textLight;
    if (cls === 'text-gray-400') styles.color = CoachCedarColors.textLighter;
    if (cls.startsWith('text-green-')) {
      if (cls === 'text-green-600') styles.color = CoachCedarColors.primary;
    }
    
    // Border radius
    if (cls === 'rounded') styles.borderRadius = BorderRadius.md;
    if (cls === 'rounded-lg') styles.borderRadius = BorderRadius.lg;
    if (cls === 'rounded-xl') styles.borderRadius = BorderRadius.xl;
    if (cls === 'rounded-full') styles.borderRadius = BorderRadius.full;
    
    // Flex
    if (cls === 'flex') styles.display = 'flex';
    if (cls === 'flex-row') styles.flexDirection = 'row';
    if (cls === 'flex-col') styles.flexDirection = 'column';
    if (cls === 'items-center') styles.alignItems = 'center';
    if (cls === 'justify-center') styles.justifyContent = 'center';
    if (cls === 'justify-between') styles.justifyContent = 'space-between';
    
    // Typography
    if (cls === 'font-bold') styles.fontWeight = 'bold';
    if (cls === 'font-semibold') styles.fontWeight = '600';
    if (cls === 'font-medium') styles.fontWeight = '500';
    if (cls.match(/^text-(xs|sm|base|lg|xl|2xl|3xl|4xl)$/)) {
      const size = cls.split('-')[1] as keyof typeof Typography;
      Object.assign(styles, Typography[size]);
    }
  });

  return styles;
};

/**
 * Common Coach Cedar styles
 * 
 * 🎨 BUTTON COLOR GUIDANCE:
 * - Primary actions (CTAs) → cedarPrime (dark green, high contrast)
 * - Page buttons (Settings, Account) → cedarLight (#5FA287) ⭐ PREFERRED - pops more
 * - Secondary actions → cedarLight (better visibility than cedarSubtle)
 * - Soft backgrounds → cedarSubtle (gentle, non-interactive)
 */
export const CoachCedarStyles = StyleSheet.create({
  // Containers
  container: {
    flex: 1,
    backgroundColor: CoachCedarColors.background,
  },
  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: CoachCedarColors.background,
  },
  
  // Cards
  card: {
    backgroundColor: CoachCedarColors.background,
    borderRadius: BorderRadius.lg,
    padding: Spacing.lg,
    marginVertical: Spacing.sm,
    ...Shadows.md,
  },
  
  // Buttons
  primaryButton: {
    backgroundColor: CoachCedarColors.cedarPrime,  // Primary CTAs (dark green)
    borderRadius: BorderRadius.lg,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButton: {
    backgroundColor: CoachCedarColors.cedarLight,  // ⭐ Page buttons (Settings, Account) - pops more!
    borderRadius: BorderRadius.lg,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  
  // Chat bubbles
  cedarBubble: {
    backgroundColor: CoachCedarColors.cedarBubble,
    borderRadius: BorderRadius.lg,
    padding: Spacing.md,
    maxWidth: '80%',
    alignSelf: 'flex-start',
  },
  userBubble: {
    backgroundColor: CoachCedarColors.userBubble,
    borderRadius: BorderRadius.lg,
    padding: Spacing.md,
    maxWidth: '80%',
    alignSelf: 'flex-end',
  },
  
  // Input
  input: {
    backgroundColor: CoachCedarColors.backgroundLight,
    borderRadius: BorderRadius.lg,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    fontSize: 16,
    color: CoachCedarColors.text,
    borderWidth: 1,
    borderColor: CoachCedarColors.border,
  },
  
  // Text styles
  heading1: {
    fontSize: 30,
    fontWeight: 'bold',
    color: CoachCedarColors.text,
    marginBottom: Spacing.md,
  },
  heading2: {
    fontSize: 24,
    fontWeight: '600',
    color: CoachCedarColors.text,
    marginBottom: Spacing.sm,
  },
  body: {
    fontSize: 16,
    lineHeight: 24,
    color: CoachCedarColors.text,
  },
  caption: {
    fontSize: 14,
    lineHeight: 20,
    color: CoachCedarColors.textLight,
  },
});

